import { useCallback, useMemo, useState } from "react";
import { useGetList, useNotify, useRefresh, useUpdate, type RaRecord } from "react-admin";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { formatListDate } from "@/utils/date-format";

type LagatOption = RaRecord & {
  receiptNo?: string | null;
  name?: string | null;
  itsNo?: string | null;
  amount?: number | null;
  receiptDate?: string | Date | null;
};

export type RazaRequestLinkLagatDialogProps = {
  open: boolean;
  onClose: () => void;
  razaRequestId: string;
  /** After linking to the request, also attach this raza event (optional). */
  defaultRazaEventId?: string | null;
};

/** Pick a lagat receipt that has no raza request yet and attach it to this request. */
const RazaRequestLinkLagatDialog = ({
  open,
  onClose,
  razaRequestId,
  defaultRazaEventId = null,
}: RazaRequestLinkLagatDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading: saving }] = useUpdate();
  const [selected, setSelected] = useState<LagatOption | null>(null);

  const { data: receipts = [], isLoading: loadingList } = useGetList("lagatReceipts", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "receiptDate", order: "DESC" },
    filter: { withoutRazaRequest: true },
  });

  const options = useMemo(() => receipts as LagatOption[], [receipts]);

  const handleClose = useCallback(() => {
    if (!saving) {
      setSelected(null);
      onClose();
    }
  }, [onClose, saving]);

  const handleLink = useCallback(() => {
    if (!selected?.id) {
      notify("Choose a receipt", { type: "warning" });
      return;
    }
    const eventId =
      typeof defaultRazaEventId === "string" && defaultRazaEventId.trim()
        ? defaultRazaEventId.trim()
        : undefined;

    update(
      "lagatReceipts",
      {
        id: selected.id,
        data: {
          razaRequestId,
          ...(eventId ? { razaEventId: eventId } : {}),
        },
        previousData: selected,
      },
      {
        onSuccess: () => {
          notify("Lagat receipt linked to this raza request", { type: "success" });
          refresh();
          handleClose();
        },
        onError: (err) => {
          const msg =
            err instanceof Error
              ? err.message
              : typeof err === "string"
                ? err
                : "Could not link receipt";
          notify(msg, { type: "error" });
        },
      }
    );
  }, [selected, razaRequestId, defaultRazaEventId, update, notify, refresh, handleClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link existing lagat receipt</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Only receipts with no raza request yet are listed (a request can hold many receipts). If a
          receipt is tied to a raza event but has no request, adjust links on the receipt edit
          screen first.
        </Typography>
        <Autocomplete
          options={options}
          loading={loadingList}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          getOptionLabel={(r) => {
            const no = r.receiptNo?.trim() || String(r.id);
            const nm = r.name?.trim();
            const its = r.itsNo?.trim();
            const amt =
              r.amount != null && !Number.isNaN(Number(r.amount))
                ? new Intl.NumberFormat(undefined, { style: "currency", currency: "INR" }).format(
                    Number(r.amount)
                  )
                : "";
            const dt = formatListDate(r.receiptDate, { empty: "" });
            return [no, nm, its, amt, dt].filter(Boolean).join(" — ");
          }}
          isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Lagat receipt"
              placeholder={loadingList ? "Loading…" : "Search by receipt no., name, or ITS"}
              margin="normal"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleLink} variant="contained" disabled={saving || !selected?.id}>
          {saving ? "Linking…" : "Link receipt"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RazaRequestLinkLagatDialog;
